import { useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";
import {
  GET_MY_SCORECARD,
  GET_INTERVIEW_SCORECARDS,
  ASSIGN_GUIDE,
  ENSURE_SCORECARD,
  REMOVE_GUIDE,
  SAVE_SCORECARD,
  SUBMIT_SCORECARD,
  ADD_SCORECARD_QUESTION,
  REMOVE_SCORECARD_QUESTION,
  UPLOAD_TRANSCRIPT,
  DELETE_TRANSCRIPT,
  EXTRACT_TRANSCRIPT_TEXT,
  GET_TRANSCRIPT_FILE_URL,
  GENERATE_SUMMARY,
  GENERATE_AI_SCORE,
} from "./operations";
import { toScorecardDisplay } from "../mappers/scorecardMapper";
import { executeMutation } from "./mutationHelpers";
import type {
  ScorecardDisplay,
  ScorecardQuestionScoreInput,
  ScorecardRecommendation,
  GuideQuestionInput,
  ScorecardQuestionSource,
  UploadedFileRef,
} from "../types";

export function useScorecard(interviewId: string | null) {
  const { federationGatewayClient } = useApolloClients();

  const { data, loading, error, refetch } = useQuery(GET_MY_SCORECARD, {
    client: federationGatewayClient,
    variables: { interviewId },
    skip: !interviewId,
    fetchPolicy: "cache-and-network",
  });

  const scorecard: ScorecardDisplay | null = useMemo(
    () => (data?.myScorecard ? toScorecardDisplay(data.myScorecard) : null),
    [data?.myScorecard],
  );

  return { scorecard, loading, error, refetch };
}

export function useInterviewScorecards(interviewId: string | null) {
  const { federationGatewayClient } = useApolloClients();

  const { data, loading, error, refetch } = useQuery(GET_INTERVIEW_SCORECARDS, {
    client: federationGatewayClient,
    variables: { interviewId },
    skip: !interviewId,
    fetchPolicy: "cache-and-network",
  });

  const scorecards = data?.interviewScorecards || [];

  return { scorecards, loading, error, refetch };
}

export function useScorecardMutations() {
  const { federationGatewayClient } = useApolloClients();

  const [assignGuide] = useMutation(ASSIGN_GUIDE, {
    client: federationGatewayClient,
    // The assigned guide is derived from the interview's scorecards, so refresh
    // them after (un)assigning to update the UI without a reload.
    refetchQueries: [GET_INTERVIEW_SCORECARDS],
    awaitRefetchQueries: false,
  });
  const [ensureScorecardMutation] = useMutation(ENSURE_SCORECARD, {
    client: federationGatewayClient,
  });
  const [removeGuide] = useMutation(REMOVE_GUIDE, {
    client: federationGatewayClient,
    refetchQueries: [GET_INTERVIEW_SCORECARDS],
    awaitRefetchQueries: false,
  });
  const [saveScorecard] = useMutation(SAVE_SCORECARD, {
    client: federationGatewayClient,
  });
  const [submitScorecard] = useMutation(SUBMIT_SCORECARD, {
    client: federationGatewayClient,
    // submit transitions status → other reviewers' list views need refresh
    refetchQueries: [GET_INTERVIEW_SCORECARDS],
    awaitRefetchQueries: false,
  });
  const [addScorecardQuestion] = useMutation(ADD_SCORECARD_QUESTION, {
    client: federationGatewayClient,
  });
  const [removeScorecardQuestion] = useMutation(REMOVE_SCORECARD_QUESTION, {
    client: federationGatewayClient,
  });
  const [uploadTranscript] = useMutation(UPLOAD_TRANSCRIPT, {
    client: federationGatewayClient,
  });
  const [deleteTranscript] = useMutation(DELETE_TRANSCRIPT, {
    client: federationGatewayClient,
  });
  // Returns transcript { id text } — Apollo normalises onto the cached
  // Transcript, so the interview-transcript query updates automatically.
  const [extractTranscriptTextMutation] = useMutation(EXTRACT_TRANSCRIPT_TEXT, {
    client: federationGatewayClient,
  });
  const [generateSummary] = useMutation(GENERATE_SUMMARY, {
    client: federationGatewayClient,
  });
  const [generateAIScore] = useMutation(GENERATE_AI_SCORE, {
    client: federationGatewayClient,
  });

  return {
    assignGuide: async (interviewId: string, guideId: string) => {
      const result = await executeMutation(
        "assignGuide",
        () => assignGuide({ variables: { interviewId, guideId } }),
        (d: any) => d.interviewGuideAssign,
      );
      return result;
    },
    // Self-provision the caller's own scorecard (idempotent). The backend only
    // creates one for an interview with an assigned guide when the caller is on
    // the panel, so this is safe to call whenever myScorecard is empty.
    ensureScorecard: async (interviewId: string) => {
      const result = await executeMutation(
        "ensureScorecard",
        () => ensureScorecardMutation({ variables: { interviewId } }),
        (d: any) => d.scorecardEnsure,
      );
      return result;
    },
    removeGuide: async (interviewId: string) => {
      const result = await executeMutation(
        "removeGuide",
        () => removeGuide({ variables: { interviewId } }),
        (d: any) => d.interviewGuideRemove,
      );
      return result;
    },
    saveScorecard: async (
      interviewId: string,
      input: {
        questions?: ScorecardQuestionScoreInput[];
        recommendation?: ScorecardRecommendation;
        finalComment?: string;
      },
    ) => {
      const result = await executeMutation(
        "saveScorecard",
        () => saveScorecard({ variables: { interviewId, input } }),
        (d: any) => d.scorecardSave,
      );
      return result;
    },
    submitScorecard: async (
      interviewId: string,
      input: {
        questions: ScorecardQuestionScoreInput[];
        recommendation: ScorecardRecommendation;
        finalComment?: string;
      },
    ) => {
      const result = await executeMutation(
        "submitScorecard",
        () => submitScorecard({ variables: { interviewId, input } }),
        (d: any) => d.scorecardSubmit,
      );
      return result;
    },
    addScorecardQuestion: async (
      interviewId: string,
      input: {
        text: string;
        scoringGuide: GuideQuestionInput["scoringGuide"];
        source: ScorecardQuestionSource;
        kssartiTopic?: string;
      },
    ) => {
      const result = await executeMutation(
        "addScorecardQuestion",
        () => addScorecardQuestion({ variables: { interviewId, input } }),
        (d: any) => d.scorecardQuestionAdd,
      );
      return result;
    },
    removeScorecardQuestion: async (interviewId: string, id: string) => {
      const result = await executeMutation(
        "removeScorecardQuestion",
        () => removeScorecardQuestion({ variables: { interviewId, id } }),
        (d: any) => d.scorecardQuestionRemove,
      );
      return result;
    },
    uploadTranscript: async (
      interviewId: string,
      input: {
        text?: string;
        textHtml?: string;
        fileName?: string;
        file?: UploadedFileRef;
      },
    ) => {
      const result = await executeMutation(
        "uploadTranscript",
        () => uploadTranscript({ variables: { interviewId, input } }),
        (d: any) => d.transcriptUpload,
      );
      return result;
    },
    removeTranscript: async (interviewId: string) => {
      const result = await executeMutation(
        "removeTranscript",
        () => deleteTranscript({ variables: { interviewId } }),
        (d: any) => d.transcriptDelete,
      );
      return result;
    },
    extractTranscriptText: async (interviewId: string) => {
      const result = await executeMutation(
        "extractTranscriptText",
        () => extractTranscriptTextMutation({ variables: { interviewId } }),
        (d: any) => d.transcriptExtractText,
      );
      return result;
    },
    // Presigned URLs are short-lived, so always fetch a fresh one at click time.
    getTranscriptFileDownloadUrl: async (
      interviewId: string,
    ): Promise<string | null> => {
      const { data } = await federationGatewayClient.query({
        query: GET_TRANSCRIPT_FILE_URL,
        variables: { interviewId },
        fetchPolicy: "network-only",
      });
      return data?.interviewTranscript?.fileDownloadUrl ?? null;
    },
    generateSummary: async (interviewId: string) => {
      const result = await executeMutation(
        "generateSummary",
        () => generateSummary({ variables: { interviewId } }),
        (d: any) => d.transcriptGenerateSummary,
      );
      return result;
    },
    generateAIScore: async (interviewId: string) => {
      const result = await executeMutation(
        "generateAIScore",
        () => generateAIScore({ variables: { interviewId } }),
        (d: any) => d.transcriptGenerateAIScore,
      );
      return result;
    },
  };
}
